import React, { useState, useEffect } from 'react';
import { ArrowLeft, Clock, User, Users, Calendar, AlertCircle, MessageSquare, Send, Upload, X, Link, FileText, Eye, Download, CheckSquare, Square, Plus, Trash2, UserPlus } from 'lucide-react';
import { createNotification } from '../../utils/api';
import { DevOpsTask, DevOpsTaskStatus, DevOpsTaskPriority } from '../../types/devopsTasks';
import { User as UserType } from '../../types';
import Avatar from '../common/Avatar';
import { toast } from '../../utils/toast';
import { readApiError } from '../common/apiError';

// GET /api/users returns realmRoles/roles arrays; `role` alone is only the primary role.
const userHasRole = (u: any, role: string): boolean =>
  ((u?.realmRoles || u?.roles || [u?.role]) as string[]).some(r => typeof r === 'string' && r.toLowerCase() === role);

interface TaskDetailProps {
  taskId: string;
  currentUser: UserType;
  onBack: () => void;
}

const TaskDetail: React.FC<TaskDetailProps> = ({ taskId, currentUser, onBack }) => {
  const [task, setTask] = useState<DevOpsTask | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [assigningTask, setAssigningTask] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [timeTracking, setTimeTracking] = useState(false);
  const [devopsUsers, setDevopsUsers] = useState<UserType[]>([]);
  const [selectedDevops, setSelectedDevops] = useState('');
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassigning, setReassigning] = useState(false);

  // Phase 1: Task Dependencies
  const [showAddDependencyModal, setShowAddDependencyModal] = useState(false);
  const [allTasks, setAllTasks] = useState<DevOpsTask[]>([]);
  const [selectedDependencyTask, setSelectedDependencyTask] = useState('');
  const [addingDependency, setAddingDependency] = useState(false);
  // dep.status on task.dependsOn[] is a snapshot taken when the dependency was
  // added and never updates — this holds each dependency's live status instead.
  const [dependencyLiveStatus, setDependencyLiveStatus] = useState<Record<string, string>>({});

  // Phase 1: Rejection with Reason
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  // Phase 1: File Preview
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [previewFile, setPreviewFile] = useState<string | null>(null);
  const [previewFileType, setPreviewFileType] = useState<string | null>(null);

  // Phase 2: Multiple Assignees
  const [showAddAssigneeModal, setShowAddAssigneeModal] = useState(false);
  const [selectedAssigneeUser, setSelectedAssigneeUser] = useState('');
  const [assigneeRole, setAssigneeRole] = useState('developer');
  const [addingAssignee, setAddingAssignee] = useState(false);
  const [removingAssignee, setRemovingAssignee] = useState<string | null>(null);

  // Phase 2: Approval Workflow
  const [showRequestApprovalModal, setShowRequestApprovalModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectApprovalModal, setShowRejectApprovalModal] = useState(false);
  const [selectedApprovers, setSelectedApprovers] = useState<string[]>([]);
  const [approvalComment, setApprovalComment] = useState('');
  const [requestingApproval, setRequestingApproval] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejectingApproval, setRejectingApproval] = useState(false);
  const [allUsers, setAllUsers] = useState<UserType[]>([]);

  // Phase 3: Tags
  const [newTag, setNewTag] = useState('');
  const [addingTag, setAddingTag] = useState(false);

  // Phase 3: Save as Template
  const [showSaveAsTemplateModal, setShowSaveAsTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => {
    fetchTask();
    fetchDevopsUsers();
    fetchAllUsers();
  }, [taskId]);

  const fetchDevopsUsers = async () => {
    try {
      const response = await fetch('/api/users');
      if (response.ok) {
        const users = await response.json();
        const devops = (Array.isArray(users) ? users : []).filter((u: UserType) => userHasRole(u, 'devops'));
        setDevopsUsers(devops);
      }
    } catch (err) {
      console.error('Failed to fetch devops users:', err);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await fetch('/api/users');
      if (response.ok) {
        const users = await response.json();
        // Filter to TL, PM, Admin roles for approvers
        setAllUsers((Array.isArray(users) ? users : []).filter((u: UserType) => ['tl', 'pm', 'admin'].some(r => userHasRole(u, r))));
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchTask = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/tasks/${taskId}`);
      if (!response.ok) throw new Error(await readApiError(response, 'Failed to fetch task'));
      const data = await response.json();
      setTask(data);
      if (data.dependsOn && data.dependsOn.length > 0) {
        fetchDependencyStatus();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  const fetchDependencyStatus = async () => {
    try {
      const response = await fetch(`/api/tasks/${taskId}/dependencies/status`);
      if (!response.ok) return;
      const data = await response.json();
      const statusMap: Record<string, string> = {};
      (data.dependencies || []).forEach((dep: any) => {
        statusMap[dep.taskId] = dep.currentStatus;
      });
      setDependencyLiveStatus(statusMap);
    } catch (err) {
      console.error('Failed to fetch dependency status:', err);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim() || !task) return;

    try {
      setSubmittingComment(true);
      setCommentError(null);
      
      const formData = new FormData();
      formData.append('userId', currentUser.id);
      formData.append('userName', currentUser.name || currentUser.email);
      formData.append('userRole', currentUser.role);
      formData.append('comment', comment.trim());
      
      // Add attachments
      attachments.forEach((file) => {
        formData.append('attachments', file);
      });

      const response = await fetch(`/api/tasks/${taskId}/comments`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error(await readApiError(response, 'Failed to add comment'));
      
      setComment('');
      setAttachments([]);
      setCommentError(null);
      await fetchTask(); // Refresh task data
      // notify creator and assigned user
      try {
        await createNotification(currentUser.id, 'Comment Added', `Your comment was added to task "${task.title}".`);
      } catch (nerr) {
        console.warn('Notification create failed:', nerr);
      }
    } catch (err) {
      // Comment text and attachments are kept so the user can retry
      const message = err instanceof Error ? err.message : 'Failed to add comment';
      setCommentError(message);
      toast(message, 'error');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setAttachments(prev => [...prev, ...newFiles]);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleAssignToMe = async () => {
    if (!task) return;

    try {
      setAssigningTask(true);
      const response = await fetch(`/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assignedTo: {
            id: currentUser.id,
            name: currentUser.name || currentUser.email,
            email: currentUser.email,
            role: currentUser.role
          },
          status: 'assigned',
          changedBy: currentUser.id,
          changedByName: currentUser.name || currentUser.email,
          updatedBy: {
            id: currentUser.id,
            name: currentUser.name || currentUser.email,
            role: currentUser.role
          }
        })
      });

      if (!response.ok) throw new Error(await readApiError(response, 'Failed to assign task'));
      
      await fetchTask();
      try {
        await createNotification(currentUser.id, 'Task Assigned', `You have been assigned task "${task.title}".`);
      } catch (nerr) {
        console.warn('Failed to create notification for assignment:', nerr);
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to assign task', 'error');
    } finally {
      setAssigningTask(false);
    }
  };

  const handleReassignTask = async () => {
    if (!task || !selectedDevops) return;

    const selectedUser = devopsUsers.find(u => u.id === selectedDevops);
    if (!selectedUser) return;

    try {
      setReassigning(true);
      const response = await fetch(`/api/tasks/${taskId}/reassign`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          newAssignee: {
            id: selectedUser.id,
            name: selectedUser.name || selectedUser.email,
            email: selectedUser.email,
            role: selectedUser.role
          },
          previousAssignee: task.assignedTo,
          reassignedBy: {
            id: currentUser.id,
            name: currentUser.name || currentUser.email,
            role: currentUser.role
          }
        })
      });

      if (!response.ok) throw new Error(await readApiError(response, 'Failed to reassign task'));
      
      setShowReassignModal(false);
      setSelectedDevops('');
      await fetchTask();
      
      try {
        await createNotification(currentUser.id, 'Task Reassigned', `Task "${task.title}" has been reassigned to ${selectedUser.name}.`);
      } catch (nerr) {
        console.warn('Failed to create notification for reassignment:', nerr);
      }
    } catch (err) {
      console.error('Failed to reassign task:', err);
      toast(err instanceof Error ? err.message : 'Failed to reassign task', 'error');
    } finally {
      setReassigning(false);
    }
  };

  const handleStatusChange = async (newStatus: DevOpsTaskStatus) => {
    if (!task) return;

    try {
      setUpdatingStatus(true);
      const response = await fetch(`/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: newStatus,
          changedBy: currentUser.id,
          changedByName: currentUser.name || currentUser.email,
          updatedBy: {
            id: currentUser.id,
            name: currentUser.name || currentUser.email,
            role: currentUser.role
          }
        })
      });

      if (!response.ok) throw new Error(await readApiError(response, 'Failed to update status'));
      
      // Auto-start time tracking when moving to "in-progress"
      if (newStatus === 'in-progress' && !task.timeTracking?.currentSession) {
        try {
          await fetch(`/api/tasks/${taskId}/time/start`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userId: currentUser.id,
              userName: currentUser.name || currentUser.email
            })
          });
        } catch (timeErr) {
          console.warn('Failed to auto-start time tracking:', timeErr);
        }
      }
      
      // Auto-complete time tracking when task is completed
      if ((newStatus === 'completed' || newStatus === 'rejected') && task.timeTracking?.currentSession) {
        try {
          const timeResponse = await fetch(`/api/tasks/${taskId}/time/complete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userId: currentUser.id
            })
          });
          
          if (!timeResponse.ok) {
            const errorData = await timeResponse.json().catch(() => ({}));
            console.warn('Failed to auto-complete time tracking:', errorData);
          }
        } catch (timeErr) {
          console.warn('Failed to auto-complete time tracking:', timeErr);
          // Don't show alert - it's not critical to task completion
        }
      }
      
      await fetchTask();
      try {
        await createNotification(currentUser.id, 'Status Updated', `Status of task "${task.title}" changed to ${newStatus}.`);
      } catch (nerr) {
        console.warn('Failed to create status notification:', nerr);
      }
    } catch (err) {
      // e.g. 400 when approval is pending, a dependency is incomplete or the workflow disallows it
      toast(err instanceof Error ? err.message : 'Failed to update status', 'error');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Time Tracking Functions
  const handleStartTimeTracking = async () => {
    if (!task) return;
    
    try {
      setTimeTracking(true);
      const response = await fetch(`/api/tasks/${taskId}/time/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: currentUser.id,
          userName: currentUser.name || currentUser.email
        })
      });

      if (!response.ok) throw new Error(await readApiError(response, 'Failed to start time tracking'));
      
      await fetchTask();
      await createNotification(currentUser.id, 'Timer Started', `Time tracking started for task "${task.title}".`);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to start time tracking', 'error');
    } finally {
      setTimeTracking(false);
    }
  };

  const handlePauseTimeTracking = async () => {
    if (!task) return;
    
    try {
      setTimeTracking(true);
      const response = await fetch(`/api/tasks/${taskId}/time/pause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: currentUser.id
        })
      });

      if (!response.ok) throw new Error(await readApiError(response, 'Failed to pause time tracking'));
      
      const data = await response.json();
      await fetchTask();
      await createNotification(currentUser.id, 'Timer Paused', `Time tracking paused for task "${task.title}". Duration: ${data.duration} minutes.`);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to pause time tracking', 'error');
    } finally {
      setTimeTracking(false);
    }
  };

  const formatTimeSpent = (minutes: number): string => {
    if (!minutes) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Priority badge colors
  const getPriorityColor = (priority: DevOpsTaskPriority) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Status badge colors
  const getStatusColor = (status: DevOpsTaskStatus) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'assigned': return 'bg-indigo-100 text-indigo-800';
      case 'in-progress': return 'bg-purple-100 text-purple-800';
      case 'testing': return 'bg-cyan-100 text-cyan-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // ====== PHASE 1: TASK DEPENDENCIES ======
  const fetchAllTasks = async () => {
    try {
      const response = await fetch('/api/tasks');
      if (response.ok) {
        const tasks = await response.json();
        // Filter out current task and completed tasks
        setAllTasks(tasks.filter((t: DevOpsTask) => 
          t._id !== taskId && t.status !== 'completed' && t.status !== 'rejected'
        ));
      }
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    }
  };

  const handleAddDependency = async () => {
    if (!selectedDependencyTask || !task) return;

    try {
      setAddingDependency(true);
      const response = await fetch(`/api/tasks/${taskId}/dependencies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dependsOnTaskId: selectedDependencyTask,
          addedBy: {
            id: currentUser.id,
            name: currentUser.name || currentUser.email
          }
        })
      });

      if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to add dependency'));
      }

      setShowAddDependencyModal(false);
      setSelectedDependencyTask('');
      await fetchTask();
      await createNotification(currentUser.id, 'Dependency Added', `Task dependency added successfully.`);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to add dependency', 'error');
    } finally {
      setAddingDependency(false);
    }
  };

  const handleRemoveDependency = async (depId: string) => {
    if (!task) return;

    try {
      const response = await fetch(`/api/tasks/${taskId}/dependencies/${depId}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error(await readApiError(response, 'Failed to remove dependency'));

      await fetchTask();
      await createNotification(currentUser.id, 'Dependency Removed', `Task dependency removed.`);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to remove dependency', 'error');
    }
  };

  // ====== PHASE 3: TAGS ======
  const handleAddTag = async () => {
    if (!newTag.trim() || !task) return;
    try {
      setAddingTag(true);
      const response = await fetch(`/api/tasks/${taskId}/tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tags: [newTag.trim()] })
      });
      if (!response.ok) throw new Error(await readApiError(response, 'Failed to add tag'));
      setNewTag('');
      await fetchTask();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to add tag', 'error');
    } finally {
      setAddingTag(false);
    }
  };

  const handleRemoveTag = async (tag: string) => {
    if (!task) return;
    try {
      const response = await fetch(`/api/tasks/${taskId}/tags/${encodeURIComponent(tag)}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error(await readApiError(response, 'Failed to remove tag'));
      await fetchTask();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to remove tag', 'error');
    }
  };

  // ====== PHASE 3: SAVE AS TEMPLATE ======
  const handleSaveAsTemplate = async () => {
    if (!templateName.trim() || !task) return;
    try {
      setSavingTemplate(true);
      const response = await fetch(`/api/tasks/${taskId}/save-as-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templateName: templateName.trim(),
          templateDescription: templateDescription.trim()
        })
      });
      if (!response.ok) throw new Error(await readApiError(response, 'Failed to save template'));
      setShowSaveAsTemplateModal(false);
      setTemplateName('');
      setTemplateDescription('');
      toast(`✅ Template "${templateName.trim()}" saved successfully!`);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to save template', 'error');
    } finally {
      setSavingTemplate(false);
    }
  };

  // ====== PHASE 1: REJECTION WITH REASON ======
  const handleRejectWithReason = async () => {
    if (!rejectionReason.trim() || !task) {
      toast('Please provide a rejection reason');
      return;
    }

    try {
      setRejecting(true);
      const response = await fetch(`/api/tasks/${taskId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reason: rejectionReason.trim(),
          rejectedBy: {
            id: currentUser.id,
            name: currentUser.name || currentUser.email
          }
        })
      });

      if (!response.ok) throw new Error(await readApiError(response, 'Failed to reject task'));

      setShowRejectModal(false);
      setRejectionReason('');
      await fetchTask();
      await createNotification(currentUser.id, 'Task Rejected', `Task "${task.title}" has been rejected.`);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to reject task', 'error');
    } finally {
      setRejecting(false);
    }
  };

  // ====== PHASE 1: FILE PREVIEW ======
  const handlePreviewFile = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    const imageTypes = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg'];
    const pdfTypes = ['pdf'];
    const textTypes = ['txt', 'log', 'json', 'xml', 'csv'];

    if (imageTypes.includes(ext || '')) {
      setPreviewFileType('image');
    } else if (pdfTypes.includes(ext || '')) {
      setPreviewFileType('pdf');
    } else if (textTypes.includes(ext || '')) {
      setPreviewFileType('text');
    } else {
      // Unsupported, just download
      window.open(`/api/tasks/files/preview/${filename}`, '_blank');
      return;
    }

    setPreviewFile(filename);
    setShowFilePreview(true);
  };

  // ====== PHASE 2: MULTIPLE ASSIGNEES ======
  const handleAddAssignee = async () => {
    if (!selectedAssigneeUser || !task) return;

    try {
      setAddingAssignee(true);
      const selectedUser = devopsUsers.find(u => u.id === selectedAssigneeUser);
      if (!selectedUser) throw new Error('User not found');

      const response = await fetch(`/api/tasks/${taskId}/assignees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: selectedUser.id,
          userName: selectedUser.name || selectedUser.email,
          userEmail: selectedUser.email,
          role: assigneeRole,
          assignedBy: {
            id: currentUser.id,
            name: currentUser.name || currentUser.email
          }
        })
      });

      if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to add assignee'));
      }

      setShowAddAssigneeModal(false);
      setSelectedAssigneeUser('');
      setAssigneeRole('developer');
      await fetchTask();
      await createNotification(currentUser.id, 'Team Member Added', `${selectedUser.name} added to task "${task.title}".`);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to add assignee', 'error');
    } finally {
      setAddingAssignee(false);
    }
  };

  const handleRemoveAssignee = async (assigneeId: string) => {
    if (!task || !window.confirm('Remove this team member from the task?')) return;

    try {
      setRemovingAssignee(assigneeId);
      const response = await fetch(`/api/tasks/${taskId}/assignees/${assigneeId}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error(await readApiError(response, 'Failed to remove assignee'));

      await fetchTask();
      await createNotification(currentUser.id, 'Team Member Removed', `Team member removed from task.`);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to remove assignee', 'error');
    } finally {
      setRemovingAssignee(null);
    }
  };

  // ====== PHASE 2: APPROVAL WORKFLOW ======
  const handleRequestApproval = async () => {
    if (!task || selectedApprovers.length === 0) {
      toast('Please select at least one approver');
      return;
    }

    try {
      setRequestingApproval(true);
      const approvers = selectedApprovers.map(approverId => {
        const user = allUsers.find(u => u.id === approverId);
        return {
          id: user?.id || '',
          name: user?.name || user?.email || '',
          email: user?.email || '',
          role: user?.role || ''
        };
      });

      const response = await fetch(`/api/tasks/${taskId}/request-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approvers,
          requestedBy: {
            id: currentUser.id,
            name: currentUser.name || currentUser.email
          }
        })
      });

      if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to request approval'));
      }

      setShowRequestApprovalModal(false);
      setSelectedApprovers([]);
      await fetchTask();
      await createNotification(currentUser.id, 'Approval Requested', `Approval request sent for task "${task.title}".`);
    } catch (err) {
      console.error('Request approval error:', err);
      toast(err instanceof Error ? err.message : 'Failed to request approval', 'error');
    } finally {
      setRequestingApproval(false);
    }
  };

  const handleApproveTask = async () => {
    if (!task) return;

    try {
      setApproving(true);
      const response = await fetch(`/api/tasks/${taskId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approvedBy: {
            id: currentUser.id,
            name: currentUser.name || currentUser.email
          },
          comment: approvalComment.trim()
        })
      });

      if (!response.ok) throw new Error(await readApiError(response, 'Failed to approve task'));

      setShowApproveModal(false);
      setApprovalComment('');
      await fetchTask();
      await createNotification(currentUser.id, 'Task Approved', `Task "${task.title}" has been approved.`);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to approve task', 'error');
    } finally {
      setApproving(false);
    }
  };

  const handleRejectApproval = async () => {
    if (!task || !approvalComment.trim()) {
      toast('Please provide a reason for rejection');
      return;
    }

    try {
      setRejectingApproval(true);
      const response = await fetch(`/api/tasks/${taskId}/reject-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rejectedBy: {
            id: currentUser.id,
            name: currentUser.name || currentUser.email
          },
          reason: approvalComment.trim()
        })
      });

      if (!response.ok) throw new Error(await readApiError(response, 'Failed to reject approval'));

      setShowRejectApprovalModal(false);
      setApprovalComment('');
      await fetchTask();
      await createNotification(currentUser.id, 'Approval Rejected', `Task approval rejected for "${task.title}".`);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to reject approval', 'error');
    } finally {
      setRejectingApproval(false);
    }
  };

  const getApprovalBadgeColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'approved': return 'bg-green-100 text-green-800 border-green-300';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isDevOps = currentUser.role === 'devops';
  const canChangeStatus = isDevOps;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading task...</p>
        </div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="space-y-4">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Tasks
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
          <span className="text-red-800">{error || 'Task not found'}</span>
        </div>
      </div>
    );
  }

  // The server rejects completion (400) while approval is pending or a dependency is
  // incomplete — explain that up front instead of offering a button that will fail.
  const incompleteDependencyCount = (task.dependsOn || []).filter(
    dep => (dependencyLiveStatus[dep.taskId] || dep.status) !== 'completed'
  ).length;
  const completionBlockedReason =
    task.approvalStatus === 'pending'
      ? 'Approval is still pending — the task can be completed once all approvers have approved it.'
      : task.approvalStatus === 'rejected'
        ? 'Approval was rejected — request approval again before completing this task.'
        : incompleteDependencyCount > 0
          ? `Blocked by ${incompleteDependencyCount} incomplete ${incompleteDependencyCount === 1 ? 'dependency' : 'dependencies'} — complete ${incompleteDependencyCount === 1 ? 'it' : 'them'} first.`
          : null;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-700 font-semibold text-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Tasks
        </button>
        <button
          onClick={() => {
            setTemplateName(task.title);
            setTemplateDescription(task.description || '');
            setShowSaveAsTemplateModal(true);
          }}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
        >
          <FileText className="w-4 h-4" />
          Save as Template
        </button>
      </div>

      {/* Task Header - Full Width */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl shadow-xl p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-4">
            <span className="text-3xl font-bold text-white bg-white/20 px-5 py-2 rounded-lg backdrop-blur-sm">
              #{task.taskNumber}
            </span>
            <div>
              <h1 className="text-2xl font-bold text-white mb-1">{task.title}</h1>
              <p className="text-blue-100 text-sm">Task ID: {taskId}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`px-4 py-2 text-sm font-bold rounded-lg uppercase shadow-md ${getPriorityColor(task.priority)} border-2`}>
              {task.priority}
            </span>
            <span className={`px-4 py-2 text-sm font-bold rounded-lg uppercase shadow-md ${getStatusColor(task.status)}`}>
              {task.status.replace('-', ' ')}
            </span>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Task Details (2/3 width) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Task Description */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide">Description</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-700 text-base leading-relaxed whitespace-pre-wrap">{task.description}</p>
            </div>
          </div>

          {/* Tags */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide">Tags</h2>
            </div>
            <div className="p-6">
              <div className="flex flex-wrap gap-2 mb-3">
                {(task.tags || []).length === 0 ? (
                  <p className="text-sm text-gray-500">No tags yet</p>
                ) : (
                  task.tags!.map((tag) => (
                    <span
                      key={tag}
                      className="flex items-center gap-1.5 px-3 py-1 bg-indigo-100 text-indigo-700 text-sm font-medium rounded-full"
                    >
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="hover:bg-indigo-200 rounded-full p-0.5"
                        title="Remove tag"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                  placeholder="Add a tag..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <button
                  onClick={handleAddTag}
                  disabled={!newTag.trim() || addingTag}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-semibold transition-colors"
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* Subtasks / Checklist */}
          <SubtasksSection
            task={task}
            currentUser={currentUser}
            onUpdate={fetchTask}
          />

          {/* Task Details Grid */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide">Task Information</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Created By */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-gray-500">
                    <User className="w-5 h-5" />
                    <p className="text-sm font-semibold uppercase tracking-wide">Created by</p>
                  </div>
                  <div className="flex items-center space-x-3 pl-7">
                    <Avatar name={task.createdBy.name} userId={task.createdBy.id} size="md" />
                    <div>
                      <p className="text-base font-bold text-gray-900">{task.createdBy.name}</p>
                      <p className="text-sm text-gray-600 capitalize">{task.createdBy.role}</p>
                    </div>
                  </div>
                </div>

                {/* Assigned To */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-gray-500">
                    <User className="w-5 h-5" />
                    <p className="text-sm font-semibold uppercase tracking-wide">Assigned to</p>
                  </div>
                  {task.assignedTo ? (
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3 pl-7">
                        <Avatar name={task.assignedTo.name} userId={task.assignedTo.id} size="md" />
                        <div>
                          <p className="text-base font-bold text-gray-900">{task.assignedTo.name}</p>
                          <p className="text-sm text-gray-600">{task.assignedTo.email}</p>
                        </div>
                      </div>
                      {/* Reassign Button - visible to assigned DevOps, TL, PM, Admin */}
                      {(task.assignedTo.id === currentUser.id || ['tl', 'pm', 'admin'].includes(currentUser.role)) && (
                        <div className="ml-7">
                          <button
                            onClick={() => setShowReassignModal(true)}
                            className="px-3 py-1.5 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 font-semibold transition-all"
                          >
                            🔄 Reassign Task
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 ml-7">
                      <p className="font-semibold text-yellow-900 text-sm mb-1">⚠️ Not Assigned</p>
                      {isDevOps && (
                        <button
                          onClick={handleAssignToMe}
                          disabled={assigningTask}
                          className="mt-2 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50 font-semibold transition-all"
                        >
                          {assigningTask ? 'Assigning...' : 'Assign to Me'}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Phase 2: Multiple Assignees / Team Members */}
                {task.assignees && task.assignees.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 text-gray-500">
                        <Users className="w-5 h-5" />
                        <p className="text-sm font-semibold uppercase tracking-wide">Team Members ({task.assignees.length})</p>
                      </div>
                      {['tl', 'pm', 'admin'].includes(currentUser.role) && (
                        <button
                          onClick={() => setShowAddAssigneeModal(true)}
                          className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                          + Add Member
                        </button>
                      )}
                    </div>
                    <div className="pl-7 space-y-2">
                      {task.assignees.map((assignee) => (
                        <div key={assignee.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                          <div className="flex items-center space-x-3">
                            <Avatar name={assignee.name} userId={assignee.id} size="sm" />
                            <div>
                              <p className="text-sm font-bold text-gray-900">{assignee.name}</p>
                              <p className="text-xs text-gray-600">{assignee.email}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                  {assignee.role}
                                </span>
                                {assignee.assignedAt && (
                                  <span className="text-xs text-gray-500">
                                    Added {new Date(assignee.assignedAt).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          {['tl', 'pm', 'admin'].includes(currentUser.role) && (
                            <button
                              onClick={() => handleRemoveAssignee(assignee.id)}
                              disabled={removingAssignee === assignee.id}
                              className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                              title="Remove team member"
                            >
                              <X size={16} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add Team Member button for empty state */}
                {(!task.assignees || task.assignees.length === 0) && ['tl', 'pm', 'admin'].includes(currentUser.role) && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-gray-500">
                      <Users className="w-5 h-5" />
                      <p className="text-sm font-semibold uppercase tracking-wide">Team Members</p>
                    </div>
                    <div className="pl-7">
                      <button
                        onClick={() => setShowAddAssigneeModal(true)}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        + Add Team Member
                      </button>
                    </div>
                  </div>
                )}

                {/* Created At */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-gray-500">
                    <Clock className="w-5 h-5" />
                    <p className="text-sm font-semibold uppercase tracking-wide">Created</p>
                  </div>
                  <p className="text-base font-bold text-gray-900 pl-7">{formatDate(task.createdAt)}</p>
                </div>

                {/* Expected Completion */}
                {task.expectedCompletionDate && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-gray-500">
                      <Calendar className="w-5 h-5" />
                      <p className="text-sm font-semibold uppercase tracking-wide">Due Date</p>
                    </div>
                    <p className="text-base font-bold text-gray-900 pl-7">{formatDate(task.expectedCompletionDate)}</p>
                  </div>
                )}

                {/* Completed At */}
                {task.completedAt && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-green-500">
                      <Clock className="w-5 h-5" />
                      <p className="text-sm font-semibold uppercase tracking-wide">Completed</p>
                    </div>
                    <p className="text-base font-bold text-green-700 pl-7">{formatDate(task.completedAt)}</p>
                  </div>
                )}

                {/* Affected Environment */}
                {task.affectedEnvironment && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-gray-500">
                      <AlertCircle className="w-5 h-5" />
                      <p className="text-sm font-semibold uppercase tracking-wide">Environment</p>
                    </div>
                    <span className="inline-block ml-7 bg-orange-100 text-orange-800 px-4 py-2 rounded-lg font-bold text-sm border border-orange-300">
                      {task.affectedEnvironment}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Time Tracking Card */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-green-600" />
                  Time Tracking
                </h2>
                {task.timeTracking && task.timeTracking.totalTimeSpent > 0 && (
                  <span className="px-4 py-2 bg-green-600 text-white rounded-lg font-bold text-sm shadow-md">
                    Total: {formatTimeSpent(task.timeTracking.totalTimeSpent)}
                  </span>
                )}
              </div>
            </div>
            <div className="p-6">
              {/* Current Session Status */}
              {task.timeTracking?.currentSession && task.timeTracking.currentSession.userId === currentUser.id ? (
                <div className="bg-green-100 border-2 border-green-400 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-green-900 text-base flex items-center">
                        <Clock className="w-5 h-5 mr-2 animate-pulse" />
                        Timer Running
                      </p>
                      <p className="text-sm text-green-700 mt-1">
                        Started: {formatDate(task.timeTracking.currentSession.startTime)}
                      </p>
                    </div>
                    <button
                      onClick={handlePauseTimeTracking}
                      disabled={timeTracking}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 font-semibold shadow-md transition-all text-sm"
                    >
                      ⏸️ Pause
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mb-4">
                  {isDevOps && task.status !== 'completed' && task.status !== 'rejected' && (
                    <button
                      onClick={handleStartTimeTracking}
                      disabled={timeTracking || (task.timeTracking?.currentSession && task.timeTracking.currentSession.userId !== currentUser.id)}
                      className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-semibold shadow-md transition-all flex items-center justify-center"
                    >
                      <Clock className="w-5 h-5 mr-2" />
                      {task.timeTracking?.currentSession ? 'Another user is tracking time' : 'Start Working'}
                    </button>
                  )}
                </div>
              )}

              {/* Time Sessions History */}
              {task.timeTracking && task.timeTracking.sessions && task.timeTracking.sessions.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">Time Sessions</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {task.timeTracking.sessions.map((session, index) => (
                      <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-gray-900">{session.userName}</span>
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded font-bold text-xs">
                            {formatTimeSpent(session.duration || 0)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600">
                          {formatDate(session.startTime)} - {session.endTime ? formatDate(session.endTime) : 'In Progress'}
                        </div>
                        <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${
                          session.status === 'active' ? 'bg-green-100 text-green-800' :
                          session.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {session.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Status Change Controls (DevOps only) */}
          {canChangeStatus && task.status !== 'completed' && task.status !== 'rejected' && (
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2 text-blue-600" />
                  Update Status
                </h2>
              </div>
              <div className="p-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-700 font-medium mb-1">
                    <strong>Workflow:</strong> NEW → ASSIGNED → IN PROGRESS → TESTING → COMPLETED
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  {task.status === 'new' && !task.assignedTo && (
                    <button
                      onClick={handleAssignToMe}
                      disabled={assigningTask}
                      className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-semibold shadow-md hover:shadow-lg transition-all flex items-center text-sm"
                    >
                      <User className="w-4 h-4 mr-2" />
                      {assigningTask ? 'Assigning...' : 'Assign to Me'}
                    </button>
                  )}
                  {(task.status === 'assigned' || task.status === 'new') && (
                    <button
                      onClick={() => handleStatusChange('in-progress')}
                      disabled={updatingStatus}
                      className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-semibold shadow-md hover:shadow-lg transition-all text-sm"
                    >
                      🚀 Start Work
                    </button>
                  )}
                  {task.status === 'in-progress' && (
                    <button
                      onClick={() => handleStatusChange('testing')}
                      disabled={updatingStatus}
                      className="px-5 py-2.5 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 font-semibold shadow-md hover:shadow-lg transition-all text-sm"
                    >
                      🧪 Move to Testing
                    </button>
                  )}
                  {(task.status === 'testing' || task.status === 'in-progress') && (
                    <button
                      onClick={() => handleStatusChange('completed')}
                      disabled={updatingStatus || !!completionBlockedReason}
                      title={completionBlockedReason || 'Mark this task as completed'}
                      className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-md hover:shadow-lg transition-all text-sm"
                    >
                      ✅ Mark Complete
                    </button>
                  )}
                  <button
                    onClick={() => setShowRejectModal(true)}
                    disabled={updatingStatus}
                    className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-semibold shadow-md hover:shadow-lg transition-all text-sm"
                  >
                    ❌ Reject with Reason
                  </button>
                </div>
                {completionBlockedReason && (task.status === 'testing' || task.status === 'in-progress') && (
                  <p className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start">
                    <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Can't mark complete yet: {completionBlockedReason}</span>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* PHASE 2: Approval Workflow */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-green-600" />
                Approval Workflow
              </h2>
            </div>
            <div className="p-6 space-y-4">
              {/* Approval Status Badge */}
              {task.requiresApproval && task.approvalStatus && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700">Status:</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase border-2 ${getApprovalBadgeColor(task.approvalStatus)}`}>
                      {task.approvalStatus === 'pending' && '⏳'} 
                      {task.approvalStatus === 'approved' && '✅'} 
                      {task.approvalStatus === 'rejected' && '❌'} 
                      {task.approvalStatus}
                    </span>
                  </div>
                  {/* Approvers List */}
                  {task.approvers && task.approvers.length > 0 && (
                    <div className="text-xs text-gray-600">
                      Approvers: {task.approvers.map(a => a.name).join(', ')}
                    </div>
                  )}
                </div>
              )}

              {/* Approval Buttons */}
              <div className="flex flex-wrap gap-3">
                {/* Request Approval Button - All users can request */}
                {!task.requiresApproval && task.status !== 'completed' && task.status !== 'rejected' && (
                  <button
                    onClick={() => setShowRequestApprovalModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold shadow-md hover:shadow-lg transition-all text-sm flex items-center"
                  >
                    📋 Request Approval
                  </button>
                )}

                {/* Approve / Reject Buttons - For designated approvers */}
                {task.requiresApproval && 
                 task.approvalStatus === 'pending' && 
                 task.approvers?.some(a => a.id === currentUser.id) && (
                  <>
                    <button
                      onClick={() => setShowApproveModal(true)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold shadow-md hover:shadow-lg transition-all text-sm flex items-center"
                    >
                      ✅ Approve
                    </button>
                    <button
                      onClick={() => setShowRejectApprovalModal(true)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold shadow-md hover:shadow-lg transition-all text-sm flex items-center"
                    >
                      ❌ Reject
                    </button>
                  </>
                )}
              </div>

              {/* Approval History Timeline */}
              {task.approvalHistory && task.approvalHistory.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">Approval History</h3>
                  <div className="space-y-3">
                    {task.approvalHistory.map((entry, index) => (
                      <div key={index} className={`border-l-4 pl-4 py-2 ${
                        entry.action === 'requested' ? 'border-blue-400 bg-blue-50' :
                        entry.action === 'approved' ? 'border-green-400 bg-green-50' :
                        'border-red-400 bg-red-50'
                      }`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`font-bold text-sm ${
                            entry.action === 'requested' ? 'text-blue-800' :
                            entry.action === 'approved' ? 'text-green-800' :
                            'text-red-800'
                          }`}>
                            {entry.action === 'requested' && '📋 Approval Requested'}
                            {entry.action === 'approved' && '✅ Approved'}
                            {entry.action === 'rejected' && '❌ Rejected'}
                          </span>
                          <span className="text-xs text-gray-600">{formatDate(entry.timestamp)}</span>
                        </div>
                        <div className="text-sm text-gray-700">
                          <strong>{entry.approver.name}</strong>
                        </div>
                        {entry.comment && (
                          <div className="text-sm text-gray-600 mt-1 italic">
                            "{entry.comment}"
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!task.requiresApproval && (
                <div className="text-sm text-gray-500 italic">
                  No approval required for this task yet. Click "Request Approval" to initiate the approval process.
                </div>
              )}
            </div>
          </div>

          {/* PHASE 1: Task Dependencies */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide flex items-center">
                <Link className="w-5 h-5 mr-2 text-orange-600" />
                Task Dependencies
              </h2>
              {(currentUser.role === 'tl' || currentUser.role === 'pm' || currentUser.role === 'admin') && (
                <button
                  onClick={() => {
                    fetchAllTasks();
                    setShowAddDependencyModal(true);
                  }}
                  className="px-3 py-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold text-sm shadow-sm transition-all"
                >
                  + Add Dependency
                </button>
              )}
            </div>
            <div className="p-6">
              {task.dependsOn && task.dependsOn.length > 0 ? (
                <div className="space-y-3">
                  {task.dependsOn.map((dep, index) => {
                    const liveStatus = dependencyLiveStatus[dep.taskId] || dep.status;
                    return (
                    <div key={index} className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border-l-4 border-orange-500">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-sm font-bold text-gray-700">#{dep.taskNumber}</span>
                          <span className={`px-2 py-0.5 text-xs font-bold rounded ${getStatusColor(liveStatus as DevOpsTaskStatus)}`}>
                            {liveStatus.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-900 font-medium">{dep.title}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Added {formatDate(dep.addedAt)} by {typeof dep.addedBy === 'string' ? dep.addedBy : (dep.addedBy as any)?.name}
                        </p>
                      </div>
                      {(currentUser.role === 'tl' || currentUser.role === 'pm' || currentUser.role === 'admin') && (
                        <button
                          onClick={() => handleRemoveDependency(dep.taskId)}
                          className="ml-3 p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          title="Remove Dependency"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    );
                  })}
                  {task.dependsOn.some(dep => (dependencyLiveStatus[dep.taskId] || dep.status) !== 'completed') && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center">
                      <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0" />
                      <p className="text-sm text-yellow-800 font-medium">
                        This task is blocked by {task.dependsOn.filter(d => (dependencyLiveStatus[d.taskId] || d.status) !== 'completed').length} incomplete dependencies
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No dependencies</p>
              )}
            </div>
          </div>

          {/* PHASE 1: Rejection Reason Display */}
          {task.status === 'rejected' && task.rejectionReason && (
            <div className="bg-white rounded-xl shadow-lg border border-red-300 overflow-hidden">
              <div className="bg-gradient-to-r from-red-50 to-pink-50 px-6 py-4 border-b border-red-200">
                <h2 className="text-lg font-bold text-red-900 uppercase tracking-wide flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
                  Rejection Reason
                </h2>
              </div>
              <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap mb-3">{task.rejectionReason}</p>
                  {task.rejectedBy && (
                    <div className="flex items-center text-xs text-gray-600">
                      <User className="w-3 h-3 mr-1" />
                      <span>Rejected by <strong>{task.rejectedBy.name}</strong> at {formatDate(task.rejectedBy.timestamp)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Status History */}
          {task.statusHistory && task.statusHistory.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide">Status History</h2>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {task.statusHistory.map((history, index) => (
                    <div key={index} className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border-l-4 border-blue-500">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getStatusColor(history.status)} font-bold text-sm shadow-sm`}>
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                          <span className={`px-3 py-1 text-xs font-bold rounded-lg ${getStatusColor(history.status)}`}>
                            {history.status.replace('-', ' ').toUpperCase()}
                          </span>
                          <span className="text-xs font-semibold text-gray-500">{formatDate(history.timestamp)}</span>
                        </div>
                        <p className="text-sm text-gray-700">
                          By <span className="font-semibold text-gray-900">{history.changedByName || history.changedBy}</span>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Comments Sidebar (1/3 width) */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden sticky top-6">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
              <h2 className="text-lg font-bold text-white flex items-center">
                <MessageSquare className="w-5 h-5 mr-2" />
                Comments ({task.comments?.length || 0})
              </h2>
            </div>

            <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              {/* Add Comment Form */}
              <form onSubmit={handleAddComment} className="mb-6">
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Write a comment..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm resize-none"
                />
                
                {/* File Upload Section */}
                <div className="mt-3">
                  <label className="flex items-center justify-center px-3 py-2 bg-gray-50 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-100 transition-all text-sm">
                    <Upload className="w-4 h-4 mr-2 text-gray-600" />
                    <span className="text-gray-700 font-medium">
                      {attachments.length > 0 ? `${attachments.length} file(s)` : 'Attach files'}
                    </span>
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.txt"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </label>
                  
                  {/* Selected Files Preview */}
                  {attachments.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {attachments.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded px-2 py-1.5 text-xs">
                          <span className="truncate flex-1 text-gray-700">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            className="ml-2 p-0.5 hover:bg-red-100 rounded"
                          >
                            <X className="w-3 h-3 text-red-600" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {commentError && (
                  <p className="mt-2 text-sm text-red-600" role="alert">{commentError}</p>
                )}
                <div className="mt-3 flex justify-end">
                  <button
                    type="submit"
                    disabled={!comment.trim() || submittingComment}
                    className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-md transition-all text-sm"
                  >
                    <Send className="w-4 h-4 mr-1.5" />
                    {submittingComment ? 'Posting...' : 'Post'}
                  </button>
                </div>
              </form>

              {/* Comments List */}
              <div className="space-y-3">
                {task.comments && task.comments.length > 0 ? (
                  task.comments.map((comment, index) => (
                    <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className="flex items-start space-x-2 mb-2">
                        <Avatar name={comment.userName} userId={comment.userId} size="sm" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-bold text-gray-900 text-sm truncate">{comment.userName}</p>
                            <span className="text-xs text-gray-500 ml-2">{formatDate(comment.timestamp).split(',')[0]}</span>
                          </div>
                          <span className="inline-block px-2 py-0.5 bg-gray-200 rounded text-xs font-medium text-gray-700 capitalize mt-0.5">
                            {comment.userRole}
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-800 text-sm leading-relaxed whitespace-pre-wrap">{comment.comment}</p>
                      
                      {/* Comment Attachments */}
                      {comment.attachments && comment.attachments.length > 0 && (
                        <div className="mt-3 space-y-1">
                          {comment.attachments.map((attachment: any, attIndex: number) => (
                            <div
                              key={attIndex}
                              className="flex items-center space-x-2 p-2 bg-white border border-gray-300 rounded hover:bg-blue-50 hover:border-blue-400 transition-all"
                            >
                              <Upload className="w-3 h-3 text-blue-600 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-900 truncate">{attachment.originalName}</p>
                              </div>
                              <div className="flex space-x-1">
                                <button
                                  onClick={() => handlePreviewFile(attachment.filename)}
                                  className="p-1.5 hover:bg-blue-100 rounded transition-colors"
                                  title="Preview"
                                >
                                  <Eye className="w-3.5 h-3.5 text-blue-600" />
                                </button>
                                <a
                                  href={attachment.path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1.5 hover:bg-green-100 rounded transition-colors"
                                  title="Download"
                                >
                                  <Download className="w-3.5 h-3.5 text-green-600" />
                                </a>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500 font-medium text-sm">No comments yet</p>
                    <p className="text-gray-400 text-xs mt-1">Be the first!</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reassignment Modal */}
      {showReassignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">🔄 Reassign Task</h3>
            <p className="text-sm text-gray-600 mb-4">
              Task will be reassigned to another DevOps member. All work progress and time tracking will be preserved.
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select DevOps Member
              </label>
              <select
                value={selectedDevops}
                onChange={(e) => setSelectedDevops(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
              >
                <option value="">-- Choose DevOps Member --</option>
                {devopsUsers
                  .filter(u => u.id !== task?.assignedTo?.id)
                  .map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name || user.email} ({user.email})
                    </option>
                  ))}
              </select>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowReassignModal(false);
                  setSelectedDevops('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleReassignTask}
                disabled={!selectedDevops || reassigning}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-all"
              >
                {reassigning ? 'Reassigning...' : 'Reassign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PHASE 3: Save as Template Modal */}
      {showSaveAsTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <FileText className="w-6 h-6 mr-2 text-indigo-600" />
              Save as Template
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Create a reusable template from this task's type, priority, and environment.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Template Name</label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Description (optional)</label>
              <textarea
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowSaveAsTemplateModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAsTemplate}
                disabled={!templateName.trim() || savingTemplate}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold transition-all"
              >
                {savingTemplate ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PHASE 1: Add Dependency Modal */}
      {showAddDependencyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Link className="w-6 h-6 mr-2 text-orange-600" />
              Add Task Dependency
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              This task will be blocked until the selected dependency is completed.
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select Task
              </label>
              <select
                value={selectedDependencyTask}
                onChange={(e) => setSelectedDependencyTask(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:ring-2 focus:ring-orange-200 max-h-60"
              >
                <option value="">-- Choose Task --</option>
                {allTasks.map(t => (
                  <option key={t._id} value={t._id}>
                    #{t.taskNumber} - {t.title} ({t.status})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowAddDependencyModal(false);
                  setSelectedDependencyTask('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleAddDependency}
                disabled={!selectedDependencyTask || addingDependency}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-all"
              >
                {addingDependency ? 'Adding...' : 'Add Dependency'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PHASE 1: Rejection Reason Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-red-900 mb-4 flex items-center">
              <AlertCircle className="w-6 h-6 mr-2 text-red-600" />
              Reject Task
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for rejecting this task. This will be sent to the task creator.
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Rejection Reason <span className="text-red-600">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why this task is being rejected..."
                rows={4}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:ring-2 focus:ring-red-200 resize-none"
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectWithReason}
                disabled={!rejectionReason.trim() || rejecting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-all"
              >
                {rejecting ? 'Rejecting...' : 'Reject Task'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PHASE 1: File Preview Modal */}
      {showFilePreview && previewFile && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 flex items-center">
                <Eye className="w-5 h-5 mr-2 text-blue-600" />
                File Preview
              </h3>
              <button
                onClick={() => {
                  setShowFilePreview(false);
                  setPreviewFile(null);
                  setPreviewFileType(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            
            <div className="flex-1 overflow-auto p-4 bg-gray-50">
              {previewFileType === 'image' && (
                <img
                  src={`/api/tasks/files/preview/${previewFile}`}
                  alt="Preview"
                  className="max-w-full max-h-full mx-auto"
                />
              )}
              {previewFileType === 'pdf' && (
                <iframe
                  src={`/api/tasks/files/preview/${previewFile}`}
                  className="w-full h-[70vh] border-0"
                  title="PDF Preview"
                />
              )}
              {previewFileType === 'text' && (
                <div className="bg-white p-4 rounded-lg shadow-inner">
                  <iframe
                    src={`/api/tasks/files/preview/${previewFile}`}
                    className="w-full h-[60vh] border-0"
                    title="Text Preview"
                  />
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-between items-center">
              <span className="text-sm text-gray-600 font-medium">{previewFile}</span>
              <a
                href={`/api/tasks/files/preview/${previewFile}`}
                download
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-all flex items-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </a>
            </div>
          </div>
        </div>
      )}

      {/* PHASE 2: Add Assignee Modal */}
      {showAddAssigneeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Users className="w-6 h-6 mr-2 text-blue-600" />
              Add Team Member
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Add additional team members to collaborate on this task.
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select Team Member <span className="text-red-600">*</span>
              </label>
              <select
                value={selectedAssigneeUser}
                onChange={(e) => setSelectedAssigneeUser(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              >
                <option value="">-- Choose Team Member --</option>
                {devopsUsers
                  .filter(u => 
                    u.id !== task?.assignedTo?.id && 
                    !task?.assignees?.some(a => a.id === u.id)
                  )
                  .map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name || user.email} ({user.email})
                    </option>
                  ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Role on Task
              </label>
              <select
                value={assigneeRole}
                onChange={(e) => setAssigneeRole(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              >
                <option value="developer">Developer</option>
                <option value="reviewer">Reviewer</option>
                <option value="tester">Tester</option>
                <option value="support">Support</option>
              </select>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowAddAssigneeModal(false);
                  setSelectedAssigneeUser('');
                  setAssigneeRole('developer');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleAddAssignee}
                disabled={!selectedAssigneeUser || addingAssignee}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-all"
              >
                {addingAssignee ? 'Adding...' : 'Add Member'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PHASE 2: Request Approval Modal */}
      {showRequestApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <AlertCircle className="w-6 h-6 mr-2 text-blue-600" />
              Request Approval
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Select one or more approvers to review this task.
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select Approvers <span className="text-red-600">*</span>
              </label>
              <div className="border-2 border-gray-300 rounded-lg p-3 max-h-60 overflow-y-auto">
                {allUsers.map(user => (
                  <label key={user.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedApprovers.includes(user.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedApprovers([...selectedApprovers, user.id]);
                        } else {
                          setSelectedApprovers(selectedApprovers.filter(id => id !== user.id));
                        }
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{user.name || user.email}</p>
                      <p className="text-xs text-gray-600">{user.role.toUpperCase()} - {user.email}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowRequestApprovalModal(false);
                  setSelectedApprovers([]);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestApproval}
                disabled={selectedApprovers.length === 0 || requestingApproval}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-all"
              >
                {requestingApproval ? 'Requesting...' : 'Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PHASE 2: Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-green-900 mb-4 flex items-center">
              <AlertCircle className="w-6 h-6 mr-2 text-green-600" />
              Approve Task
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              You are about to approve this task. You can optionally provide a comment.
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Comment (Optional)
              </label>
              <textarea
                value={approvalComment}
                onChange={(e) => setApprovalComment(e.target.value)}
                placeholder="Add any notes or comments..."
                rows={3}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 resize-none"
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowApproveModal(false);
                  setApprovalComment('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleApproveTask}
                disabled={approving}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-all"
              >
                {approving ? 'Approving...' : '✅ Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PHASE 2: Reject Approval Modal */}
      {showRejectApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-red-900 mb-4 flex items-center">
              <AlertCircle className="w-6 h-6 mr-2 text-red-600" />
              Reject Approval
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for rejecting this task approval.
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Rejection Reason <span className="text-red-600">*</span>
              </label>
              <textarea
                value={approvalComment}
                onChange={(e) => setApprovalComment(e.target.value)}
                placeholder="Explain why this task is being rejected..."
                rows={4}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:ring-2 focus:ring-red-200 resize-none"
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowRejectApprovalModal(false);
                  setApprovalComment('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectApproval}
                disabled={!approvalComment.trim() || rejectingApproval}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-all"
              >
                {rejectingApproval ? 'Rejecting...' : '❌ Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Subtasks Section Component
interface SubtasksSectionProps {
  task: DevOpsTask;
  currentUser: UserType;
  onUpdate: () => void;
}

const SubtasksSection: React.FC<SubtasksSectionProps> = ({ task, currentUser, onUpdate }) => {
  const [showAddSubtask, setShowAddSubtask] = useState(false);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [addingSubtask, setAddingSubtask] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [selectedSubtaskId, setSelectedSubtaskId] = useState<string | null>(null);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [allUsers, setAllUsers] = useState<UserType[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (showReassignModal) {
      fetchAllUsers();
    }
  }, [showReassignModal]);

  const fetchAllUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch('/api/users');
      if (response.ok) {
        const users = await response.json();
        setAllUsers(users);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAddSubtask = async () => {
    if (!newSubtaskTitle.trim()) return;

    setAddingSubtask(true);
    try {
      const response = await fetch(`/api/tasks/${task._id}/subtasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newSubtaskTitle.trim() })
      });

      if (response.ok) {
        setNewSubtaskTitle('');
        setShowAddSubtask(false);
        onUpdate();
      } else {
        toast(await readApiError(response, 'Failed to add subtask'), 'error');
      }
    } catch (err) {
      console.error('Error adding subtask:', err);
      toast(err instanceof Error ? err.message : 'Failed to add subtask', 'error');
    } finally {
      setAddingSubtask(false);
    }
  };

  const handleToggleSubtask = async (subtaskId: string) => {
    try {
      const response = await fetch(`/api/tasks/${task._id}/subtasks/${subtaskId}/toggle`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: currentUser.id,
          userName: currentUser.name
        })
      });

      if (response.ok) {
        onUpdate();
      } else {
        toast(await readApiError(response, 'Failed to update subtask'), 'error');
      }
    } catch (err) {
      console.error('Error toggling subtask:', err);
      toast(err instanceof Error ? err.message : 'Failed to update subtask', 'error');
    }
  };

  const handleDeleteSubtask = async (subtaskId: string) => {
    if (!confirm('Are you sure you want to delete this subtask?')) return;

    try {
      const response = await fetch(`/api/tasks/${task._id}/subtasks/${subtaskId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        onUpdate();
      } else {
        toast(await readApiError(response, 'Failed to delete subtask'), 'error');
      }
    } catch (err) {
      console.error('Error deleting subtask:', err);
      toast(err instanceof Error ? err.message : 'Failed to delete subtask', 'error');
    }
  };

  const handleReassignSubtask = async () => {
    if (!selectedSubtaskId || !selectedAssignee) return;

    try {
      const user = allUsers.find(u => u.id === selectedAssignee);
      if (!user) return;

      const response = await fetch(`/api/tasks/${task._id}/subtasks/${selectedSubtaskId}/reassign`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assignedTo: {
            id: user.id,
            name: user.name,
            email: user.email
          }
        })
      });

      if (response.ok) {
        setShowReassignModal(false);
        setSelectedSubtaskId(null);
        setSelectedAssignee('');
        onUpdate();
      } else {
        toast(await readApiError(response, 'Failed to reassign subtask'), 'error');
      }
    } catch (err) {
      console.error('Error reassigning subtask:', err);
      toast(err instanceof Error ? err.message : 'Failed to reassign subtask', 'error');
    }
  };

  const completedCount = task.subtasks?.filter(st => st.completed).length || 0;
  const totalCount = task.subtasks?.length || 0;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-green-50 to-emerald-100 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide flex items-center">
              <CheckSquare className="w-5 h-5 mr-2 text-green-600" />
              Subtasks & Checklist
            </h2>
            {totalCount > 0 && (
              <p className="text-sm text-gray-600 mt-1">
                {completedCount} of {totalCount} completed ({progressPercent}%)
              </p>
            )}
          </div>
          <button
            onClick={() => setShowAddSubtask(true)}
            className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 font-semibold transition-all flex items-center"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Subtask
          </button>
        </div>
        {totalCount > 0 && (
          <div className="mt-3 bg-white rounded-full h-2 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-green-500 to-emerald-600 h-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        )}
      </div>

      <div className="p-6">
        {/* Add Subtask Form */}
        {showAddSubtask && (
          <div className="mb-4 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              New Subtask
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newSubtaskTitle}
                onChange={(e) => setNewSubtaskTitle(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddSubtask()}
                placeholder="Enter subtask title..."
                className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200"
                autoFocus
              />
              <button
                onClick={handleAddSubtask}
                disabled={!newSubtaskTitle.trim() || addingSubtask}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-semibold transition-all"
              >
                {addingSubtask ? '...' : 'Add'}
              </button>
              <button
                onClick={() => {
                  setShowAddSubtask(false);
                  setNewSubtaskTitle('');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Subtasks List */}
        {task.subtasks && task.subtasks.length > 0 ? (
          <div className="space-y-2">
            {task.subtasks
              .sort((a, b) => a.order - b.order)
              .map((subtask) => (
                <div
                  key={subtask._id}
                  className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                    subtask.completed
                      ? 'bg-green-50 border-green-200'
                      : 'bg-white border-gray-200 hover:border-green-300'
                  }`}
                >
                  <div className="flex items-center space-x-3 flex-1">
                    <button
                      onClick={() => handleToggleSubtask(subtask._id)}
                      className="flex-shrink-0 focus:outline-none"
                    >
                      {subtask.completed ? (
                        <CheckSquare className="w-6 h-6 text-green-600" />
                      ) : (
                        <Square className="w-6 h-6 text-gray-400 hover:text-green-500" />
                      )}
                    </button>
                    <div className="flex-1">
                      <p className={`text-base ${
                        subtask.completed 
                          ? 'line-through text-gray-500' 
                          : 'text-gray-900 font-medium'
                      }`}>
                        {subtask.title}
                      </p>
                      {subtask.assignedTo && (
                        <p className="text-xs text-gray-600 mt-1">
                          👤 Assigned to: <span className="font-semibold">{subtask.assignedTo.name}</span>
                        </p>
                      )}
                      {subtask.completed && subtask.completedBy && (
                        <p className="text-xs text-green-600 mt-1">
                          ✓ Completed by <span className="font-semibold">{subtask.completedBy.name}</span>
                          {subtask.completedAt && ` on ${new Date(subtask.completedAt).toLocaleDateString()}`}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => {
                        setSelectedSubtaskId(subtask._id);
                        setSelectedAssignee(subtask.assignedTo?.id || '');
                        setShowReassignModal(true);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                      title="Assign to user"
                    >
                      <UserPlus className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteSubtask(subtask._id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      title="Delete subtask"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <CheckSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No subtasks yet. Click "Add Subtask" to create one.</p>
          </div>
        )}
      </div>

      {/* Reassign Subtask Modal */}
      {showReassignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <UserPlus className="w-6 h-6 mr-2 text-blue-600" />
              Assign Subtask
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select Team Member
              </label>
              {loadingUsers ? (
                <p className="text-sm text-gray-500">Loading users...</p>
              ) : (
                <select
                  value={selectedAssignee}
                  onChange={(e) => setSelectedAssignee(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                >
                  <option value="">Unassigned</option>
                  {allUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.role})
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowReassignModal(false);
                  setSelectedSubtaskId(null);
                  setSelectedAssignee('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleReassignSubtask}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-all"
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskDetail;
